import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { saveAs } from 'file-saver';
import { handleError } from 'spw-redux/modules/UI/messages/actions';
import {
  Download,
  DownloadFailedTemplate,
  DownloadStatus,
  DownloadTemplate,
  DownloadVM,
  UserDownload,
} from 'spw-programming-projects/downloads/types';
import {
  parseDownloadTemplateToVM,
  parseUITemplateToConfig,
} from 'spw-programming-projects/downloads/utils';
import { IAppState } from 'spw-redux/reducer';
import moment from 'moment';
import {
  selectDownloadsInProgress,
  selectDownloadTemplates,
  selectFailedDownloadsTemplates,
  selectFinishedProjectDownloads,
  selectNumberOfDownloadsInProgress,
  selectTemplatesWithDownloadsInProgressMap,
} from 'spw-redux/modules/downloads/selectors';
import {
  selectAltWorkpointsBreakdown,
  selectBreakdown,
  selectSelectedLevels,
} from 'spw-redux/modules/program-summary/esa/selectors';
import { APIResponse } from 'spw-redux/types';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import {
  ProgrammingReportAttributes,
  ReportAttributes,
} from 'spw-redux/modules/reports/types';
import { convertBase64StringToBlob } from 'spw-shared/utils';
import { selectCurrentSpaceTypeColors } from 'spw-redux/modules/space_types/selectors';
import { SpaceTypeColor } from 'spw-redux/modules/space_types/types';
import {
  CREATE_DOWNLOAD,
  CREATE_DOWNLOAD_TEMPLATE,
  createDownload,
  createDownloadFailed,
  createDownloadSucceeded,
  createDownloadTemplate,
  createDownloadTemplateFailed,
  createDownloadTemplateSucceeded,
  DELETE_DOWNLOAD,
  DELETE_DOWNLOAD_TEMPLATE,
  deleteDownload,
  deleteDownloadTemplate,
  deleteDownloadTemplateSucceeded,
  DOWNLOAD_TEMPLATE,
  downloadTemplate as downloadTemplateAction,
  GET_DOWNLOAD_TEMPLATE_DETAIL,
  GET_DOWNLOAD_TEMPLATES,
  GET_DOWNLOADS,
  GET_DOWNLOADS_IN_PROGRESS,
  GET_NUMBER_OF_DOWNLOADS_IN_PROGRESS,
  getDownloadsInProgressSucceeded,
  getDownloadTemplateDetail,
  getDownloadTemplateDetailSucceeded,
  getDownloadTemplates,
  getDownloadTemplatesSucceeded,
  getFinishedProjectDownloadsSucceeded,
  PREPARE_AND_EXECUTE_REPORT_DOWNLOAD,
  prepareAndExecuteReportDownload,
  reportDownloadFailed,
  reportDownloadSucceeded,
  setFailedDownloadsTemplates,
  setNumberOfDownloadsInProgress,
  UPDATE_DOWNLOAD_TEMPLATE,
  updateDownloadTemplate,
  updateDownloadTemplateFailed,
  updateDownloadTemplateSucceeded,
  setTemplatesWithDownloadsInProgress,
  CANCEL_DOWNLOADS_IN_PROGRESS,
  downloadWorksheetSpreadsheet,
  DOWNLOAD_WORKSHEET_SPREADSHEET,
  PREPARE_AND_EXECUTE_BENCHMARK_REPORT_DOWNLOAD,
  prepareAndExecuteBenchmarkReportDownload,
  PREPARE_AND_EXECUTE_ESA_REPORT_DOWNLOAD,
  prepareAndExecuteESAReportDownload,
  PREPARE_AND_EXECUTE_PROGRAMMING_REPORT_DOWNLOAD,
  prepareAndExecuteProgrammingReportDownload,
} from './actions';
import {
  createDownload as createDownloadApi,
  createDownloadFromTemplate as createDownloadFromTemplateApi,
  createDownloadTemplate as createDownloadTemplateApi,
  deleteDownload as deleteDownloadApi,
  deleteDownloadTemplate as deleteDownloadTemplateApi,
  downloadZipFile,
  getDownloads as getDownloadsApi,
  getDownloadsAndTemplate,
  getDownloadsAndTemplatesInProgress,
  getDownloadTemplate as getDownloadTemplateApi,
  getDownloadTemplates as getDownloadTemplatesApi,
  getNumberOfDownloadsInProgress as getNumberOfDownloadsInProgressApi,
  cancelDownloadsInProgress as cancelDownloadsInProgressApi,
  updateDownloadTemplate as updateDownloadTemplateApi,
} from '../../../api/downloads';
import { getReportRequest } from './utils';
import {
  currentProject as selectCurrentProject,
  selectCurrentProjectColors,
} from '../projects/selectors';
import { IProject } from '../projects/types';
import {
  exportReport as exportReportApi,
  downloadWorksheetSpreadsheet as downloadWorksheetSpreadsheetApi,
} from '../../../api/reports';
import {
  AlternativeWorkpointsBreakdown,
  ISpacesBreakDown,
  ProgramSummaryParams,
} from '../program-summary/types';
import { selectCurrentScenario } from '../calculators/selectors';
import { PlainProgramScenario } from '../../../models/Scenario';
import ESAReportFactory from '../../../models/esa/ESAReportFactory';
import {
  selectCurrentBechmarkReport,
  selectCurrentBenchmarkReportCategoriesMap,
  selectCurrentBenchmarkReportIconsMap,
  selectSelectedBenchmarkProjects,
} from '../benchmark-reports/selectors';
import {
  BenchmarkProject,
  BenchmarkReport,
  BenchmarkReportCategoriesMap,
  BenchmarkReportIconsMap,
} from '../benchmark-reports/types';
import BenchmarkReportExportable from '../../../benchmark-reports/models/benchmark-report-exportable';
import ProgrammingReportFactory from '../../../models/programming/ProgrammingReportFactory';

function* updateDownloadTemplatesAndFinishedProjectDownloads(
  updatedFinishedProjectDownloads: DownloadVM[],
  newDownloadTemplates: DownloadTemplate[],
  newFailedDownloadsTemplates: DownloadFailedTemplate[]
) {
  yield put(
    getFinishedProjectDownloadsSucceeded(updatedFinishedProjectDownloads)
  );
  yield put(getDownloadTemplatesSucceeded(newDownloadTemplates));
  yield put(setFailedDownloadsTemplates(newFailedDownloadsTemplates));
}

/**
 * Downloads zip files and add downloads that were successfully completed
 * to the project's download history.
 * @param number[] previousDownloadsInProgress - List of downloads that were in progress
 * */
function* downloadFilesAndShowErrorMessages(
  previousDownloadsInProgress: Download[]
) {
  const downloadsInProgress: Download[] = yield select(
    selectDownloadsInProgress
  );
  // A new map is created to search for downloads that are no longer in progress.
  // This will avoid having to iterate over the previousDownloadsInProgress list several times
  const downloadsInProgressMap = downloadsInProgress.reduce(
    (map: { [key: number]: number }, download: Download) => {
      return {
        ...map,
        [download.id]: download.id,
      };
    },
    {}
  );

  const completedDownloads = previousDownloadsInProgress
    .filter(
      (previousDownloadInProgress: Download) =>
        !downloadsInProgressMap[previousDownloadInProgress.id]
    )
    .map((d) => d.id);

  if (completedDownloads) {
    const response: { data: { downloads: UserDownload[] } } = yield call(
      getDownloadsAndTemplate,
      {
        ids: completedDownloads,
      }
    );
    const { downloads } = response.data;

    let updatedFinishedProjectDownloads: DownloadVM[] = yield select(
      selectFinishedProjectDownloads
    );
    updatedFinishedProjectDownloads = updatedFinishedProjectDownloads.slice();

    let newDownloadTemplates: DownloadTemplate[] = yield select(
      selectDownloadTemplates
    );
    newDownloadTemplates = newDownloadTemplates.slice();

    let newFailedDownloadsTemplates: DownloadFailedTemplate[] = yield select(
      selectFailedDownloadsTemplates
    );
    newFailedDownloadsTemplates = newFailedDownloadsTemplates.slice();

    for (const userDownload of downloads) {
      const { download } = userDownload;
      if (download.status === DownloadStatus.DONE) {
        yield call(downloadZipFile, download);

        const isDownloadAlreadyInFinishedList = updatedFinishedProjectDownloads.find(
          (finishedProjectDownload: Download) =>
            finishedProjectDownload.id === download.id
        );

        if (!isDownloadAlreadyInFinishedList) {
          updatedFinishedProjectDownloads.unshift({
            ...download,
            isDownloadingZipFile: false,
            isDownloadBeingDeleted: false,
            insertedAt: moment
              .utc(download.insertedAt, 'YYYY-MM-DD HH:mm:ss')
              .local(),
          });

          yield call(
            updateDownloadTemplatesAndFinishedProjectDownloads,
            updatedFinishedProjectDownloads,
            newDownloadTemplates,
            newFailedDownloadsTemplates
          );
        }
      } else if (download.status === DownloadStatus.FAILED) {
        const isTemplateAlreadyInDownloadTemplates = newDownloadTemplates.find(
          (downloadTemplate: DownloadTemplate) =>
            downloadTemplate.id === userDownload.template.id
        );

        if (!isTemplateAlreadyInDownloadTemplates) {
          newDownloadTemplates.unshift(userDownload.template);
        }

        const isTemplateAlreadyInFailedDownloadsTemplates = newFailedDownloadsTemplates.find(
          (failedDownloadTemplate: DownloadFailedTemplate) =>
            failedDownloadTemplate.downloadTemplate.id ===
            userDownload.template.id
        );
        if (!isTemplateAlreadyInFailedDownloadsTemplates) {
          newFailedDownloadsTemplates.push({
            isNewTemplate: !isTemplateAlreadyInDownloadTemplates,
            downloadTemplate: userDownload.template,
          });
        }

        yield call(
          updateDownloadTemplatesAndFinishedProjectDownloads,
          updatedFinishedProjectDownloads,
          newDownloadTemplates,
          newFailedDownloadsTemplates
        );
      }
    }
  }
}

function* prepareAndExecuteESAReportDownloadCall(
  action: ReturnType<typeof prepareAndExecuteESAReportDownload>
) {
  try {
    const { outputFileName, reportAttributes } = action.payload;
    const project: IProject = yield select(selectCurrentProject);
    const breakdown: ISpacesBreakDown = yield select(selectBreakdown);
    const alternativeWorkpointsBreakdown: AlternativeWorkpointsBreakdown = yield select(
      selectAltWorkpointsBreakdown
    );
    const levels: ProgramSummaryParams = yield select(selectSelectedLevels);
    const currentSpaceTypeColors: {
      [index: string]: SpaceTypeColor;
    } = yield select(selectCurrentSpaceTypeColors);
    const currentProjectColors: {
      subcategories: Record<string, string>;
    } = yield select(selectCurrentProjectColors);

    const esaReport = ESAReportFactory.create({
      ...reportAttributes,
      floorIds: reportAttributes.floorIds ?? levels.floorIds,
      buildingIds: reportAttributes.buildingIds ?? levels.buildingIds,
      currentSpaceTypeColors,
      currentProjectColors,
      project,
      alternativeWorkpointsBreakdown,
      breakdown,
    });

    const reportRequest = esaReport.getReportRequest();

    const { data }: APIResponse<typeof exportReportApi> = yield call(
      exportReportApi,
      reportRequest,
      outputFileName,
      reportRequest.downloadAsZip
    );
    const {
      report: { file, name },
    } = data;

    const format = name.split('.')?.pop();
    const blob = convertBase64StringToBlob(file, format);

    saveAs(blob, outputFileName);
    yield put(reportDownloadSucceeded());
  } catch (e) {
    yield put(handleError(e));
    yield put(reportDownloadFailed());
  }
}

function* prepareAndExecuteProgrammingReportDownloadCall(
  action: ReturnType<typeof prepareAndExecuteProgrammingReportDownload>
) {
  try {
    const { outputFileName, reportAttributes } = action.payload;
    const project: IProject = yield select(selectCurrentProject);

    const programmingReport = ProgrammingReportFactory.create({
      ...reportAttributes,
      project,
    } as ProgrammingReportAttributes);

    const reportRequest = programmingReport.getReportRequest();

    const { data }: APIResponse<typeof exportReportApi> = yield call(
      exportReportApi,
      reportRequest,
      outputFileName,
      reportRequest.downloadAsZip
    );

    const {
      report: { file, name },
    } = data;

    const format = name.split('.')?.pop();
    const blob = convertBase64StringToBlob(file, format);

    saveAs(blob, outputFileName);
    yield put(reportDownloadSucceeded());
  } catch (e) {
    yield put(handleError(e));
    yield put(reportDownloadFailed());
  }
}

function* prepareAndExecuteReportDownloadCall(
  action: ReturnType<typeof prepareAndExecuteReportDownload>
) {
  try {
    const { zipName } = action.payload;
    const appState: IAppState = yield select((state: IAppState) => state);

    const reportAttributes = action.payload
      .reportAttributes as ReportAttributes;
    // TODO: Move getReportRequest code to a class that implements the Report interface
    const reportRequest = getReportRequest(reportAttributes, zipName, appState);

    const newZipName = reportRequest.zipName;

    const { data }: APIResponse<typeof exportReportApi> = yield call(
      exportReportApi,
      reportRequest,
      newZipName
    );

    const {
      report: { file, name },
    } = data;

    const format = name.split('.')?.pop();
    const blob = convertBase64StringToBlob(file, format);

    saveAs(blob, name);
    yield put(reportDownloadSucceeded());
  } catch (e) {
    yield put(handleError(e));
    yield put(reportDownloadFailed());
  }
}

function* prepareAndExecuteBenchmarkReportDownloadCall(
  action: ReturnType<typeof prepareAndExecuteBenchmarkReportDownload>
) {
  try {
    const { outputFileName, format, isIndividualCardsExport } = action.payload;
    const currentBenchmarkReport: BenchmarkReport = yield select(
      selectCurrentBechmarkReport
    );
    const selectedBenchmarkProjects: BenchmarkProject[] = yield select(
      selectSelectedBenchmarkProjects
    );

    const currentReportCategoriesMap: BenchmarkReportCategoriesMap = yield select(
      selectCurrentBenchmarkReportCategoriesMap
    );

    const currentReportIconsMap: BenchmarkReportIconsMap = yield select(
      selectCurrentBenchmarkReportIconsMap
    );

    const exportable = new BenchmarkReportExportable({
      format,
      benchmarkReportId: currentBenchmarkReport.id,
      isIndividualCardsExport,
      selectedBenchmarkProjects,
      benchmarkReportCategories: currentReportCategoriesMap,
      benchmarkReportIcons: currentReportIconsMap,
    });
    const reportRequest = exportable.getReportRequest();

    const { data }: APIResponse<typeof exportReportApi> = yield call(
      exportReportApi,
      reportRequest,
      outputFileName
    );

    const {
      report: { file },
    } = data;

    const blob = convertBase64StringToBlob(
      file,
      isIndividualCardsExport ? 'zip' : format
    );
    saveAs(blob, outputFileName);
    yield put(reportDownloadSucceeded());
  } catch (e) {
    yield put(handleError(e));
    yield put(reportDownloadFailed());
  }
}

function* downloadWorksheetSpreadsheetCall(
  action: ReturnType<typeof downloadWorksheetSpreadsheet>
) {
  try {
    const currentScenario: PlainProgramScenario = yield select(
      selectCurrentScenario
    );
    const { rowsOpen } = action.payload;

    const project: IProject = yield select(selectCurrentProject);

    const {
      data,
    }: APIResponse<typeof downloadWorksheetSpreadsheetApi> = yield call(
      downloadWorksheetSpreadsheetApi,
      {
        scenarioId: isTotalProgram(currentScenario) ? null : currentScenario.id,
        totalProgramId: isTotalProgram(currentScenario)
          ? currentScenario.id
          : null,
        ...action.payload,
      }
    );

    const blob = convertBase64StringToBlob(data.spreadsheet, 'xlsx');
    saveAs(
      blob,
      `${project.projectName}_${currentScenario.name}_Program Worksheet (${
        rowsOpen ? 'Open' : 'Closed'
      }).xlsx`
    );
    yield put(reportDownloadSucceeded());
  } catch (e) {
    yield put(handleError(e));
    yield put(reportDownloadFailed());
  }
}

const applyDefaultTemplateConfigs = (t: DownloadTemplate) => {
  const defaultConfig = [
    {
      format: 'png',
      showClientFacingNames: false,
      showRsf: false,
    },
    {
      format: 'pdf',
      showClientFacingNames: false,
      showRsf: false,
    },
  ];
  return {
    ...t,
    formatConfigs: t.formatConfigs?.length ? t.formatConfigs : defaultConfig,
  } as DownloadTemplate;
};

function* getDownloadTemplatesCall() {
  try {
    const currentProject: IProject = yield select(selectCurrentProject);
    const result: {
      data: { downloadTemplates: DownloadTemplate[] };
    } = yield call(getDownloadTemplatesApi, currentProject.id);
    yield put(
      getDownloadTemplatesSucceeded(
        result.data.downloadTemplates.map(applyDefaultTemplateConfigs)
      )
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getDownloadTemplateDetailCall(
  action: ReturnType<typeof getDownloadTemplateDetail>
) {
  try {
    const result: {
      data: { downloadTemplate: DownloadTemplate };
    } = yield call(getDownloadTemplateApi, action.payload);
    const downloadTemplateVM = parseDownloadTemplateToVM(
      applyDefaultTemplateConfigs(result.data.downloadTemplate)
    );
    yield put(getDownloadTemplateDetailSucceeded(downloadTemplateVM));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteDownloadTemplateCall(
  action: ReturnType<typeof deleteDownloadTemplate>
) {
  try {
    yield call(deleteDownloadTemplateApi, action.payload);
    yield put(deleteDownloadTemplateSucceeded());
    yield put(getDownloadTemplates());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* createDownloadTemplateCall(
  action: ReturnType<typeof createDownloadTemplate>
) {
  const {
    template,
    navigateBack,
    shouldStartDownloadAfterSaving,
  } = action.payload;
  try {
    const currentProject: IProject = yield select(selectCurrentProject);
    const downloadConfigs = parseUITemplateToConfig(template);
    const APITemplate: DownloadTemplate = {
      name: template.name,
      projectId: currentProject.id,
      downloadConfigs,
      formatConfigs: template.formatConfigs,
    };
    const {
      data: { downloadTemplate },
    }: { data: { downloadTemplate: DownloadTemplate } } = yield call(
      createDownloadTemplateApi,
      APITemplate
    );
    if (navigateBack) navigateBack();
    yield put(createDownloadTemplateSucceeded());
    if (shouldStartDownloadAfterSaving)
      yield put(
        downloadTemplateAction({
          templateId: downloadTemplate.id,
          downloadName: template.downloadName,
        })
      );
  } catch (e) {
    yield put(createDownloadTemplateFailed());
    yield put(handleError(e));
  }
}

function* updateDownloadTemplateCall(
  action: ReturnType<typeof updateDownloadTemplate>
) {
  const { template, navigateBack, shouldDownloadAfterSaving } = action.payload;
  try {
    const currentProject: IProject = yield select(selectCurrentProject);
    const downloadConfigs = parseUITemplateToConfig(template);
    const APITemplate: DownloadTemplate = {
      id: template.id,
      name: template.name,
      projectId: currentProject.id,
      downloadConfigs,
      formatConfigs: template.formatConfigs,
    };
    const {
      data: { downloadTemplate },
    }: APIResponse<typeof updateDownloadTemplateApi> = yield call(
      updateDownloadTemplateApi,
      APITemplate
    );
    if (navigateBack) navigateBack();
    const downloadTemplates: DownloadTemplate[] = yield select(
      selectDownloadTemplates
    );
    const updatedDownloadTemplates = downloadTemplates.map((dt) => {
      if (dt.id === template.id) return { ...dt, ...APITemplate };
      return dt;
    });
    yield put(updateDownloadTemplateSucceeded());
    yield put(getDownloadTemplatesSucceeded(updatedDownloadTemplates));
    if (shouldDownloadAfterSaving)
      yield put(
        downloadTemplateAction({
          templateId: downloadTemplate.id,
          downloadName: template.downloadName,
        })
      );
  } catch (e) {
    yield put(updateDownloadTemplateFailed());
    yield put(handleError(e));
  }
}

function* downloadTemplateCall(
  action: ReturnType<typeof downloadTemplateAction>
) {
  try {
    const { templateId, downloadName, navigateBack } = action.payload;
    if (navigateBack) navigateBack();

    const templatesWithDownloadsInProgressMap: {
      [templateId: number]: boolean;
    } = yield select(selectTemplatesWithDownloadsInProgressMap);
    yield put(
      setTemplatesWithDownloadsInProgress({
        ...templatesWithDownloadsInProgressMap,
        [templateId]: true,
      })
    );

    const result: {
      data: { download: Download };
    } = yield call(createDownloadFromTemplateApi, templateId, downloadName);

    const { download } = result.data;
    const downloadsInProgress: Download[] = yield select(
      selectDownloadsInProgress
    );

    const isDownloadAlreadyInProgress = downloadsInProgress.find(
      (downloadInProgress) => downloadInProgress.id === download.id
    );

    if (!isDownloadAlreadyInProgress) {
      const updatedDownloadsInProgress = downloadsInProgress.slice();
      updatedDownloadsInProgress.push(download);
      yield put(getDownloadsInProgressSucceeded(updatedDownloadsInProgress));
      yield put(
        setNumberOfDownloadsInProgress(updatedDownloadsInProgress.length)
      );
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getDownloadsCall() {
  try {
    const currentProject: IProject = yield select(selectCurrentProject);

    const result: {
      data: { downloads: Download[] };
    } = yield call(getDownloadsApi, { projectId: currentProject.id });

    if (currentProject?.id) {
      const { downloads } = result.data;

      const finishedProjectDownloads = downloads.map(
        (download: Download): DownloadVM => {
          return {
            ...download,
            insertedAt: moment.utc(download.insertedAt, 'YYYY-MM-DD HH:mm:ss'),
            isDownloadingZipFile: false,
            isDownloadBeingDeleted: false,
          };
        }
      );

      yield put(getFinishedProjectDownloadsSucceeded(finishedProjectDownloads));
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getDownloadsInProgressCall() {
  try {
    const result: {
      data: {
        downloads: { download: Download; template: DownloadTemplate }[];
      };
    } = yield call(getDownloadsAndTemplatesInProgress, {});

    const { downloads: downloadsAndTemplatesInProgress } = result.data;

    const downloadsInProgress: Download[] = [];
    const templatesWithDownloadsInProgressMap: {
      [templateId: number]: boolean;
    } = {};
    downloadsAndTemplatesInProgress.forEach((d) => {
      downloadsInProgress.push(d.download);
      if (d.template) {
        templatesWithDownloadsInProgressMap[d.template.id] = true;
      }
    });
    yield put(getDownloadsInProgressSucceeded(downloadsInProgress));
    yield put(
      setTemplatesWithDownloadsInProgress(templatesWithDownloadsInProgressMap)
    );
    yield put(setNumberOfDownloadsInProgress(downloadsInProgress.length));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* createDownloadCall(action: ReturnType<typeof createDownload>) {
  try {
    const currentProject: IProject = yield select(selectCurrentProject);
    const { name, template, navigateBack } = action.payload;
    const downloadConfigs = parseUITemplateToConfig(template);

    const APIDownload: Partial<Download> = {
      name,
      projectId: currentProject.id,
      downloadConfigs,
      formatConfigs: template.formatConfigs,
    };

    const response: { data: { download: Download } } = yield call(
      createDownloadApi,
      APIDownload
    );
    const { download } = response.data;
    const downloadsInProgress: Download[] = yield select(
      selectDownloadsInProgress
    );

    const isDownloadAlreadyInProgress = downloadsInProgress.find(
      (d) => d.id === download.id
    );
    if (!isDownloadAlreadyInProgress) {
      const updatedDownloadsInProgress = downloadsInProgress.slice();
      updatedDownloadsInProgress.push(download);

      yield put(getDownloadsInProgressSucceeded(updatedDownloadsInProgress));
      yield put(
        setNumberOfDownloadsInProgress(updatedDownloadsInProgress.length)
      );
    }
    if (navigateBack) navigateBack();
    yield put(createDownloadSucceeded());
  } catch (e) {
    yield put(createDownloadFailed());
    yield put(handleError(e));
  }
}

function* deleteDownloadCall(action: ReturnType<typeof deleteDownload>) {
  try {
    const { id } = action.payload;
    yield call(deleteDownloadApi, id);
    const finishedProjectDownloads: Download[] = yield select(
      selectFinishedProjectDownloads
    );
    const filteredProgrammingDownloads = finishedProjectDownloads
      .filter((download: Download) => download.id !== id)
      .slice();

    yield put(
      getFinishedProjectDownloadsSucceeded(filteredProgrammingDownloads)
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getNumberOfDownloadsInProgressCall() {
  try {
    const {
      data: { numberOfDownloadsInProgress },
    } = yield call(getNumberOfDownloadsInProgressApi);

    const previousNumberOfDownloadsInProgress: number[] = yield select(
      selectNumberOfDownloadsInProgress
    );

    if (previousNumberOfDownloadsInProgress !== numberOfDownloadsInProgress) {
      const previousDownloadsInProgress: Download[] = yield select(
        selectDownloadsInProgress
      );

      yield call(getDownloadsInProgressCall);
      yield put(setNumberOfDownloadsInProgress(numberOfDownloadsInProgress));
      yield call(
        downloadFilesAndShowErrorMessages,
        previousDownloadsInProgress
      );
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* cancelDownloadsInProgressCall() {
  try {
    yield call(cancelDownloadsInProgressApi);
    yield put(setNumberOfDownloadsInProgress(0));
    yield put(setTemplatesWithDownloadsInProgress({}));
  } catch (e) {
    yield put(handleError(e));
  }
}

export default function* saga() {
  yield all([
    takeLatest(
      PREPARE_AND_EXECUTE_REPORT_DOWNLOAD,
      prepareAndExecuteReportDownloadCall
    ),
    takeLatest(
      PREPARE_AND_EXECUTE_ESA_REPORT_DOWNLOAD,
      prepareAndExecuteESAReportDownloadCall
    ),
    takeLatest(
      PREPARE_AND_EXECUTE_PROGRAMMING_REPORT_DOWNLOAD,
      prepareAndExecuteProgrammingReportDownloadCall
    ),
    takeLatest(
      PREPARE_AND_EXECUTE_BENCHMARK_REPORT_DOWNLOAD,
      prepareAndExecuteBenchmarkReportDownloadCall
    ),
    takeLatest(
      DOWNLOAD_WORKSHEET_SPREADSHEET,
      downloadWorksheetSpreadsheetCall
    ),
    takeLatest(GET_DOWNLOAD_TEMPLATES, getDownloadTemplatesCall),
    takeLatest(GET_DOWNLOAD_TEMPLATE_DETAIL, getDownloadTemplateDetailCall),
    takeLatest(CREATE_DOWNLOAD_TEMPLATE, createDownloadTemplateCall),
    takeLatest(UPDATE_DOWNLOAD_TEMPLATE, updateDownloadTemplateCall),
    takeLatest(DELETE_DOWNLOAD_TEMPLATE, deleteDownloadTemplateCall),
    takeLatest(DOWNLOAD_TEMPLATE, downloadTemplateCall),
    takeLatest(GET_DOWNLOADS, getDownloadsCall),
    takeLatest(GET_DOWNLOADS_IN_PROGRESS, getDownloadsInProgressCall),
    takeLatest(
      GET_NUMBER_OF_DOWNLOADS_IN_PROGRESS,
      getNumberOfDownloadsInProgressCall
    ),
    takeLatest(CREATE_DOWNLOAD, createDownloadCall),
    takeLatest(DELETE_DOWNLOAD, deleteDownloadCall),
    takeLatest(CANCEL_DOWNLOADS_IN_PROGRESS, cancelDownloadsInProgressCall),
  ]);
}
